import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axiosInstance';
import { NavLink } from 'react-router-dom';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend, LineChart, ReferenceLine
} from 'recharts';
import {
  TrendUp, TrendDown, Clock, CheckCircle, ArrowsClockwise,
  Rocket, PlayCircle, StopCircle, Coins, Percent, SpinnerGap, FilePdf, Gear, Lightning
} from '@phosphor-icons/react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
}).format(v || 0);
const CHANNEL_COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444'];
const ALGO_OPTIONS = [
  { value: 'MULTILATERAL_NETTING', label: 'Multilateral Netting' },
  { value: 'HYBRID_SMART',        label: 'Hybrid Smart' },
  { value: 'PRIORITY_BASED',      label: 'Priority Based' },
  { value: 'TIME_OPTIMIZED',      label: 'Time Optimized' },
  { value: 'LIQUIDITY_OPTIMIZED', label: 'Liquidity Optimized' },
];

export const Dashboard = () => {
  const [statistics, setStatistics] = useState({
    total_payments: 0, pending_payments: 0, completed_payments: 0,
    netted_payments: 0, inbound_amount: 0, outbound_amount: 0,
    pool_balance: 0, is_simulation_active: false,
    is_simulation_initialized: false, netting_cycles: 0,
    total_borrowed: 0, total_interest: 0, cb_interest_balance: 0, mm_interest_balance: 0,
    init_status: 'idle', init_progress: 0, init_target: 150
  });
  const [liquidityHistory, setLiquidityHistory] = useState([]);
  const [nettingTimeline, setNettingTimeline]   = useState([]);
  const [kpiSnapshots, setKpiSnapshots]         = useState([]);
  const [deficitStatus, setDeficitStatus]       = useState(null);
  const [config, setConfig]                 = useState(null);
  const [channels, setChannels]             = useState([]);
  const [initStatusData, setInitStatusData] = useState(null);
  const [actionLoading, setActionLoading]   = useState(false);
  const [pdfFilename, setPdfFilename]       = useState(null);
  const [stoppedAnalytics, setStoppedAnalytics] = useState(null);
  const initPollingRef = useRef(null);
  const [showSettingsTooltip, setShowSettingsTooltip] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [throughput, setThroughput] = useState(0);
  const prevProcessedRef = useRef(null);
  const prevTimeRef = useRef(null);

  // ── Single source of truth for button visibility ──────────────────────
  // simState comes from MongoDB via /api/statistics — persists across restarts.
  // Values: idle | initializing | pending_approval | initialized | running | draining | stopped
  const simState = statistics?.sim_state || 'idle';

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 3000);
    // Auto-show settings tooltip for 3 seconds on mount
    const tipTimer = setTimeout(() => setShowSettingsTooltip(true), 500);
    const hideTimer = setTimeout(() => setShowSettingsTooltip(false), 4000);
    return () => { clearInterval(id); stopInitPolling(); clearTimeout(tipTimer); clearTimeout(hideTimer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-recover init-status data when simState is pending_approval but initStatusData is missing
  // (happens after page refresh or server restart)
  useEffect(() => {
    if (simState === 'pending_approval' && !initStatusData) {
      const fetchInitStatus = async () => {
        try {
          const r = await api.get(`${API}/simulation/init-status`);
          if (r.data?.result) setInitStatusData(r.data);
        } catch (_) {}
      };
      fetchInitStatus();
    }
  }, [simState, initStatusData]); // eslint-disable-line react-hooks/exhaustive-deps

  const startInitPolling = () => {
    stopInitPolling();
    initPollingRef.current = setInterval(async () => {
      try {
        const r = await api.get(`${API}/simulation/init-status`);
        if (['generating', 'netting'].includes(r.data.status)) {
          // In-progress: update progress bar
          setInitStatusData(r.data);
        } else if (r.data.status === 'pending_borrowing_approval') {
          // Keep initStatusData so the borrowing approval card has its details
          setInitStatusData(r.data);
          stopInitPolling();
          await fetchData();
        } else {
          // Done (complete / ready / idle / aborted): clear overlay, refresh state
          stopInitPolling();
          setInitStatusData(null);
          await fetchData();
        }
      } catch(e) { console.error('Init polling error:', e); }
    }, 800);
  };

  const stopInitPolling = () => {
    if (initPollingRef.current) { clearInterval(initPollingRef.current); initPollingRef.current = null; }
  };

  const fetchData = async () => {
    try {
      const [sR, hR, dR, cR, chR, tR, kR] = await Promise.allSettled([
        api.get(`${API}/statistics`),
        api.get(`${API}/liquidity/history?limit=6`),
        api.get(`${API}/liquidity/deficit-status`),
        api.get(`${API}/config`),
        api.get(`${API}/channels`),
        api.get(`${API}/netting/timeseries`),
        api.get(`${API}/simulation/kpi-snapshots`),
      ]);
      if (sR.status === 'fulfilled') {
        setStatistics(sR.value.data);
        // ── Throughput calculation ──
        const currentProcessed = (sR.value.data?.completed_payments || 0) + (sR.value.data?.netted_payments || 0);
        const now = Date.now();
        if (prevProcessedRef.current !== null && prevTimeRef.current !== null) {
          const delta = currentProcessed - prevProcessedRef.current;
          const elapsed = (now - prevTimeRef.current) / 1000;
          if (elapsed > 0 && delta >= 0) {
            setThroughput(Math.round((delta / elapsed) * 10) / 10);
          }
        }
        prevProcessedRef.current = currentProcessed;
        prevTimeRef.current = now;
        // Pick up stop results when drain completes
        if (sR.value.data?.sim_state === 'stopped' && sR.value.data?.stop_results && !pdfFilename) {
          setPdfFilename(sR.value.data.stop_results.pdf_filename);
          setStoppedAnalytics(sR.value.data.stop_results.analytics);
          toast.success('Simulation stopped — accounts settled');
        }
      }
      if (hR.status === 'fulfilled') setLiquidityHistory(hR.value.data);
      if (dR.status === 'fulfilled') setDeficitStatus(dR.value.data);
      if (cR.status === 'fulfilled') setConfig(cR.value.data);
      if (chR.status === 'fulfilled') setChannels(chR.value.data);
      if (tR.status === 'fulfilled') setNettingTimeline(tR.value.data);
      if (kR.status === 'fulfilled') setKpiSnapshots(kR.value.data);
      setLoaded(true);
    } catch (e) { console.error('Fetch data error:', e); setLoaded(true); }
  };

  const handleInitialize = async () => {
    try {
      setActionLoading(true);
      toast.info('Starting initialization…');
      setInitStatusData({ status: 'generating', progress: 0, target: 150 });
      setPdfFilename(null);
      setStoppedAnalytics(null);
      await api.post(`${API}/simulation/initialize`);
      startInitPolling();
    } catch (e) { toast.error('Failed to initialize simulation'); }
    finally { setActionLoading(false); }
  };

  const handleApproveBorrowing = async (source) => {
    try {
      setActionLoading(true);
      await api.post(`${API}/simulation/approve-borrowing`, { source });
      toast.success(`Borrowing from ${source === 'CENTRAL_BANK' ? 'Central Bank' : 'Money Market'} approved`);
      setInitStatusData(null);
      fetchData();
    } catch (e) { toast.error('Failed to approve borrowing'); }
    finally { setActionLoading(false); }
  };

  const handleStart = async () => {
    try {
      setActionLoading(true);
      await api.post(`${API}/simulation/start`);
      toast.success('Simulation started');
      fetchData();
    } catch (e) { toast.error('Failed to start'); }
    finally { setActionLoading(false); }
  };

  const handleStop = async () => {
    try {
      setActionLoading(true);
      await api.post(`${API}/simulation/stop`);
      toast.success('Draining pending payments...');
      fetchData();
    } catch (e) { toast.error('Failed to stop'); }
    finally { setActionLoading(false); }
  };

  const handleReset = async () => {
    try {
      await api.post(`${API}/simulation/reset`);
      toast.success('Reset complete');
      setInitStatusData(null);
      setPdfFilename(null);
      setStoppedAnalytics(null);
      setKpiSnapshots([]);
      setThroughput(0);
      prevProcessedRef.current = null;
      prevTimeRef.current = null;
      stopInitPolling();
      fetchData();
    } catch (e) { toast.error('Failed to reset'); }
  };

  const handleResolveDeficit = async (source) => {
    try {
      setActionLoading(true);
      await api.post(`${API}/liquidity/resolve-deficit`, { source });
      toast.success(`Deficit resolved via ${source === 'CENTRAL_BANK' ? 'Central Bank' : 'Money Market'}`);
      fetchData();
    } catch (e) { toast.error('Failed to resolve deficit'); }
    finally { setActionLoading(false); }
  };

  const handleAlgorithmChange = async (algorithm) => {
    try {
      await api.post(`${API}/simulation/change-algorithm`, { selected_algorithm: algorithm });
      toast.success(`Algorithm: ${ALGO_OPTIONS.find(o => o.value === algorithm)?.label}`);
      fetchData();
    } catch (e) { toast.error('Failed to change algorithm'); }
  };

  const simStartMs = statistics?.simulation_start_time
    ? new Date(statistics.simulation_start_time).getTime() : null;

  const chartData = liquidityHistory.slice(-6).map(s => {
    const snapshotMs = new Date(s.timestamp).getTime();
    const elapsed = simStartMs ? Math.round((snapshotMs - simStartMs) / 1000) : 0;
    const entry = {
      t: simStartMs
        ? `${elapsed}s`
        : new Date(s.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      total: Math.round(s.total_liquidity_requirement || 0)
    };
    const reqMap = s.channel_requirements || {};
    channels.forEach(ch => { entry[ch.code] = Math.round(reqMap[ch.id] || 0); });
    return entry;
  });

  const hasAlerts = deficitStatus?.has_active_deficits;
  const allChartZero = chartData.length > 0 && chartData.every(d => (d.total || 0) === 0);

  // Progress bar during generation
  const initProgress = initStatusData?.progress || statistics?.init_progress || 0;
  const initTarget   = initStatusData?.target   || statistics?.init_target   || 150;

  return (
    <div className="space-y-4 fade-in">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-black tracking-tight text-gray-900">Dashboard</h1>
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
              simState === 'running'
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                : simState === 'initialized' || simState === 'pending_approval'
                  ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                  : simState === 'initializing'
                    ? 'bg-amber-50 border-amber-300 text-amber-700'
                    : 'bg-gray-100 border-gray-200 text-gray-500'
            }`}
            data-testid="simulation-status"
          >
            {simState === 'running' && <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full status-pulse" />}
            {simState === 'running' ? 'Live' : simState === 'initialized' ? 'Ready' : simState === 'initializing' ? 'Initializing…' : simState === 'pending_approval' ? 'Approval Needed' : 'Idle'}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <TooltipProvider delayDuration={200}>
            <Tooltip open={showSettingsTooltip} onOpenChange={setShowSettingsTooltip}>
              <TooltipTrigger asChild>
                <NavLink to="/settings" data-testid="settings-btn" className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50 transition-all">
                  <Gear size={17} weight="bold" />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Explore here
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
           CONTROL SECTION — Clean state machine. Always shows exactly one
           actionable element based on simState from MongoDB.
           ════════════════════════════════════════════════════════════════════ */}
      {(() => {
        // While first fetch is in-flight, show skeleton
        if (!loaded) {
          return <div className="w-full h-12 rounded-lg bg-gray-100 animate-pulse" data-testid="button-skeleton" />;
        }

        switch (simState) {
          case 'idle':
            return (
              <Button
                onClick={handleInitialize}
                disabled={actionLoading}
                className="w-full h-12 text-sm font-bold tracking-wide flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700"
                data-testid="initialize-btn"
              >
                {actionLoading
                  ? <SpinnerGap size={18} weight="bold" className="animate-spin" />
                  : <Rocket size={18} weight="fill" />}
                {actionLoading ? 'Initializing…' : 'Initialize Simulation'}
              </Button>
            );

          case 'initializing':
            return (
              <Card className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="init-progress-card">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-indigo-900">
                    {initStatusData?.status === 'netting'
                      ? 'Performing multilateral netting…'
                      : `Generating payments… ${initProgress} / ${initTarget}`}
                  </p>
                  <span className="text-xs font-mono text-indigo-500">{Math.round((initProgress / initTarget) * 100)}%</span>
                </div>
                <div className="w-full bg-indigo-200 rounded-full h-1.5">
                  <div
                    className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(100, (initProgress / initTarget) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-indigo-500 mt-2">Graph is updating in real time</p>
              </Card>
            );

          case 'pending_approval':
            // If server restarted and lost init data, show reset instead of blank
            if (!initStatusData?.result) {
              return (
                <div className="space-y-2">
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 font-semibold" data-testid="pending-approval-recovery-notice">
                    Session state requires re-initialization (server was restarted).
                  </div>
                  <Button
                    onClick={handleReset}
                    className="w-full h-12 text-sm font-bold tracking-wide flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700"
                    data-testid="pending-approval-reset-btn"
                  >
                    <ArrowsClockwise size={18} weight="bold" />
                    Reset &amp; Re-initialize
                  </Button>
                </div>
              );
            }
            // Normal borrowing approval card
            const shortfall   = initStatusData.result.shortfall || 0;
            const cbRate      = initStatusData.result.central_bank_rate || 5;
            const mmRate      = initStatusData.result.money_market_rate || 6.25;
            const cbInterest  = +(shortfall * cbRate / 100 / 365).toFixed(2);
            const mmInterest  = +(shortfall * mmRate / 100 / 365).toFixed(2);
            return (
              <Card className="p-4 bg-white border border-red-200 rounded-lg" data-testid="borrowing-approval-card">
                <p className="text-sm font-bold text-red-800 mb-1">External Borrowing Required — Approval Needed</p>
                <p className="text-xs text-gray-500 mb-3">Pool funds insufficient. Select a lender — interest is deducted from pool immediately upon approval, then net deficit is allocated to channels.</p>
                <div className="grid grid-cols-3 gap-2 text-xs font-mono mb-4">
                  <div className="bg-red-50 rounded-md px-3 py-2">
                    <p className="text-red-500 mb-0.5">Pool Deficit</p>
                    <p className="font-bold text-red-700">{fmt(shortfall)}</p>
                  </div>
                  <div className="bg-blue-50 rounded-md px-3 py-2">
                    <p className="text-blue-500 mb-0.5">CB Gross ({cbRate}%/yr)</p>
                    <p className="font-bold text-blue-700">{fmt(shortfall + cbInterest)}</p>
                    <p className="text-gray-400 text-[10px]">+{fmt(cbInterest)} interest</p>
                  </div>
                  <div className="bg-violet-50 rounded-md px-3 py-2">
                    <p className="text-violet-500 mb-0.5">MM Gross ({mmRate}%/yr)</p>
                    <p className="font-bold text-violet-700">{fmt(shortfall + mmInterest)}</p>
                    <p className="text-gray-400 text-[10px]">+{fmt(mmInterest)} interest</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={() => handleApproveBorrowing('CENTRAL_BANK')} disabled={actionLoading}
                    className="h-8 px-3 text-xs bg-blue-700 hover:bg-blue-800 font-bold flex items-center gap-1.5"
                    data-testid="approve-cb-borrowing-btn">
                    {actionLoading ? <SpinnerGap size={12} weight="bold" className="animate-spin" /> : null}
                    Borrow (Central Bank)
                  </Button>
                  <Button onClick={() => handleApproveBorrowing('MONEY_MARKET')} disabled={actionLoading}
                    className="h-8 px-3 text-xs bg-violet-700 hover:bg-violet-800 font-bold flex items-center gap-1.5"
                    data-testid="approve-mm-borrowing-btn">
                    {actionLoading ? <SpinnerGap size={12} weight="bold" className="animate-spin" /> : null}
                    Borrow (Money Mkt)
                  </Button>
                </div>
              </Card>
            );

          case 'initialized':
            return (
              <>
                <Button
                  onClick={handleStart}
                  disabled={actionLoading}
                  className="w-full h-12 text-sm font-bold tracking-wide flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="start-simulation-btn"
                >
                  {actionLoading
                    ? <SpinnerGap size={18} weight="bold" className="animate-spin" />
                    : <PlayCircle size={18} weight="fill" />}
                  {actionLoading ? 'Starting…' : 'Start Simulation'}
                </Button>
                {statistics?.init_status === 'ready' && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 font-semibold" data-testid="auto-approved-notice">
                    Net liquidity requirement is zero — no fund movement needed. Ready to start.
                  </div>
                )}
              </>
            );

          case 'running':
            return (
              <Button
                onClick={handleStop}
                disabled={actionLoading}
                className="w-full h-12 text-sm font-bold tracking-wide flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700"
                data-testid="stop-simulation-btn"
              >
                {actionLoading
                  ? <SpinnerGap size={18} weight="bold" className="animate-spin" />
                  : <StopCircle size={18} weight="fill" />}
                {actionLoading ? 'Stopping…' : 'Stop Simulation'}
              </Button>
            );

          case 'draining':
            return (
              <div className="w-full flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="draining-indicator">
                <SpinnerGap size={18} weight="bold" className="animate-spin text-amber-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-amber-800 font-bold text-sm">Draining pending payments...</p>
                  <p className="text-amber-600 text-xs mt-0.5">
                    {((statistics?.pending_breakdown?.interbank || 0) + (statistics?.pending_breakdown?.external || 0))} current remaining &middot; Processing in parallel across channels
                  </p>
                </div>
              </div>
            );

          case 'stopped':
          default:
            // 'stopped' or any unknown state — show Reset
            return (
              <Button
                onClick={handleReset}
                className="w-full h-12 text-sm font-bold tracking-wide flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700"
                data-testid="reset-simulation-btn"
              >
                <ArrowsClockwise size={18} weight="bold" />
                Reset Simulation
              </Button>
            );
        }
      })()}

      {/* PDF Download banner after stop */}
      {pdfFilename && (
        <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2.5">
          <FilePdf size={18} weight="duotone" className="text-indigo-600 flex-shrink-0" />
          <p className="text-xs font-semibold text-indigo-800 flex-1">Simulation report ready</p>
          <a
            href={`${API}/simulation/report/${pdfFilename}`}
            target="_blank" rel="noreferrer"
            className="text-xs font-bold text-indigo-600 hover:text-indigo-800 underline"
            data-testid="download-report-btn"
          >
            Download PDF
          </a>
        </div>
      )}

      {/* Analytics highlight card after simulation stops */}
      {stoppedAnalytics && (
        <Card className="p-0 overflow-hidden bg-white border border-emerald-200 rounded-lg" data-testid="analytics-card">
          <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between">
            <p className="text-xs font-bold text-emerald-900 uppercase tracking-wide">Simulation Performance</p>
            <span className="text-xs text-emerald-600 font-mono font-semibold px-2.5 py-1 bg-white border border-emerald-200 rounded-full">
              {stoppedAnalytics.algorithm?.replace(/_/g,' ')}
            </span>
          </div>
          <div className={`grid ${stoppedAnalytics.algorithm === 'MULTILATERAL_NETTING' ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'} gap-0 divide-x divide-gray-100`}>
            {[
              { key: 'SER', label: 'Settlement Efficiency', value: `${(stoppedAnalytics.ser ?? 0).toFixed(1)}%`, color: 'text-emerald-700', bg: 'bg-emerald-50', testid: 'analytics-ser', hint: 'Higher is better' },
              { key: 'BDR', label: 'Borrowing Dependency', value: `${(stoppedAnalytics.bdr ?? 0).toFixed(2)}%`, color: 'text-indigo-700', bg: '', testid: 'analytics-bdr', hint: 'Lower is better' },
              { key: 'LPR', label: 'Liquidity Productivity', value: `${(stoppedAnalytics.lpr ?? 0).toFixed(2)}x`, color: 'text-amber-700', bg: '', testid: 'analytics-lpr', hint: 'Higher is better' },
              ...(stoppedAnalytics.algorithm === 'MULTILATERAL_NETTING' ? [{
                key: 'NER', label: 'Netting Efficiency', value: `${(stoppedAnalytics.netting_efficiency ?? 0).toFixed(1)}%`, color: 'text-purple-700', bg: '', testid: 'analytics-ner', hint: 'Higher is better'
              }] : [])
            ].map(c => (
              <div key={c.key} className={`px-4 py-3 ${c.bg}`} data-testid={c.testid}>
                <p className="text-[10px] text-gray-400 font-medium mb-0.5 uppercase tracking-wide">{c.label}</p>
                <p className={`text-xs font-black uppercase tracking-widest mb-1.5 ${c.color}`}>{c.key}</p>
                <p className={`text-base font-black font-mono ${c.color}`}>{c.value}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{c.hint}</p>
              </div>
            ))}
          </div>
          <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
            <p className="text-[10px] text-gray-400">SER: settled/processed+pending · BDR: borrowed/total liquidity · LPR: settled value/total liquidity{stoppedAnalytics.algorithm === 'MULTILATERAL_NETTING' ? ' · NER: netted/total payments' : ''}</p>
          </div>
        </Card>
      )}

      {/* Stat Cards — Liquidity Row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Pool Bal',      value: fmt(statistics?.pool_balance),         icon: <Coins size={14} weight="bold" className="text-indigo-500" />,   testid: 'pool-balance-card' },
          { label: 'Borrowed',      value: fmt(statistics?.total_borrowed),        icon: <TrendDown size={14} weight="bold" className="text-red-400" />,   testid: 'borrowed-card' },
          { label: 'Interest',      value: fmt(statistics?.total_interest),        icon: <Percent size={14} weight="bold" className="text-amber-500" />,   testid: 'interest-card' },
        ].map(card => (
          <Card key={card.testid} className="p-3 bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={card.testid}>
            <div className="flex items-center gap-1.5 mb-1.5">{card.icon}<span className="text-xs text-gray-500 uppercase tracking-wide font-medium truncate">{card.label}</span></div>
            <p className="text-sm font-black font-mono text-gray-900 truncate">{card.value}</p>
          </Card>
        ))}
      </div>

      {/* CB / MM Interest Accounts */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'CB Interest',   value: fmt(statistics?.cb_interest_balance), testid: 'cb-interest-card',   color: 'text-blue-700' },
          { label: 'MM Interest',   value: fmt(statistics?.mm_interest_balance), testid: 'mm-interest-card',   color: 'text-purple-700' },
        ].map(card => (
          <Card key={card.testid} className="p-3 bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={card.testid}>
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1.5">{card.label}</p>
            <p className={`text-sm font-black font-mono truncate ${card.color}`}>{card.value}</p>
          </Card>
        ))}
      </div>

      {/* Stat Cards — Payment Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total',    value: statistics?.total_payments || 0,    icon: <ArrowsClockwise size={14} weight="bold" className="text-indigo-500" />, testid: 'total-payments-card' },
          { label: 'Completed', value: statistics?.completed_payments || 0, icon: <CheckCircle size={14} weight="bold" className="text-emerald-500" />,   testid: 'completed-payments-card' },
          { label: 'Pending',   value: statistics?.pending_breakdown?.future || 0,   icon: <Clock size={14} weight="bold" className="text-amber-500" />,           testid: 'pending-payments-card' },
          { label: 'Netted',    value: statistics?.netted_payments || 0,  icon: <ArrowsClockwise size={14} weight="bold" className="text-indigo-400" />, testid: 'netted-payments-card' },
        ].map(card => (
          <Card key={card.testid} className="p-3 bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={card.testid}>
            <div className="flex items-center gap-1.5 mb-1.5">{card.icon}<span className="text-xs text-gray-500 uppercase tracking-wide font-medium">{card.label}</span></div>
            <p className="text-sm font-black font-mono text-gray-900">{card.value}</p>
          </Card>
        ))}
      </div>

      {/* DEFICIT NOTIFICATION FLOW — only approval-required alerts shown here */}
      {hasAlerts && (
        <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 space-y-3" data-testid="deficit-alert-panel">
          <p className="text-red-700 font-black text-sm uppercase tracking-wide">Liquidity Deficit — Approval Required</p>
          <div className="space-y-2">
            {deficitStatus.active_deficits.map(d => (
              <div key={d.id} className="bg-white rounded-lg border border-red-200 p-3 text-xs font-mono space-y-1">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-800">{d.channel_name}</span>
                  <span className="text-red-600 font-black">{fmt(d.deficit_amount)} deficit</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <div className="bg-blue-50 rounded p-1.5">
                    <p className="text-blue-500 mb-0.5">CB Gross ({deficitStatus.central_bank_rate}%/yr)</p>
                    <p className="font-bold text-blue-700">{fmt(d.cb_gross)} <span className="text-gray-400">(+{fmt(d.cb_interest)} int.)</span></p>
                  </div>
                  <div className="bg-violet-50 rounded p-1.5">
                    <p className="text-violet-500 mb-0.5">MM Gross ({deficitStatus.money_market_rate}%/yr)</p>
                    <p className="font-bold text-violet-700">{fmt(d.mm_gross)} <span className="text-gray-400">(+{fmt(d.mm_interest)} int.)</span></p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-red-200 pt-3">
            <p className="text-gray-600 text-xs mb-2">Select lender — interest is deducted from pool at approval:</p>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => handleResolveDeficit('CENTRAL_BANK')}
                disabled={actionLoading}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm font-bold transition-colors"
                data-testid="resolve-central-bank-btn"
              >
                {actionLoading && <SpinnerGap size={14} weight="bold" className="animate-spin" />}
                Borrow from Central Bank
              </button>
              <button
                onClick={() => handleResolveDeficit('MONEY_MARKET')}
                disabled={actionLoading}
                className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm font-bold transition-colors"
                data-testid="resolve-money-market-btn"
              >
                {actionLoading && <SpinnerGap size={14} weight="bold" className="animate-spin" />}
                Borrow from Money Market
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Payments Breakdown — current queue only */}
      {(simState === 'running' || simState === 'draining') && (() => {
        const bd = statistics?.pending_breakdown || {};
        const interbank = bd.interbank || 0;
        const external = bd.external || 0;
        const current = interbank + external;
        if (current === 0) return null;
        const maxVal = Math.max(interbank, external, 1);
        return (
          <Card className="p-4 bg-white border border-gray-200 rounded-lg" data-testid="pending-breakdown">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900">Pending Payments</h2>
              <span className="text-xs font-mono font-bold text-gray-500">{current} in current queue</span>
            </div>
            <div className="space-y-2">
              {[
                { label: 'Interbank', count: interbank, color: 'bg-indigo-500' },
                { label: 'External',  count: external,  color: 'bg-amber-500' },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-16 text-right font-medium">{item.label}</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                    <div
                      className={`h-full ${item.color} rounded transition-all duration-500`}
                      style={{ width: `${(item.count / maxVal) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono font-bold text-gray-900 w-8 text-right">{item.count}</span>
                </div>
              ))}
            </div>
          </Card>
        );
      })()}

      {/* Throughput Gauge — visible during running and draining */}
      {(simState === 'running' || simState === 'draining') && (
        <Card className="p-0 overflow-hidden bg-white border border-gray-200 rounded-lg" data-testid="throughput-gauge">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lightning size={16} weight="fill" className={throughput > 0 ? 'text-emerald-500' : 'text-gray-300'} />
              <div>
                <p className="text-xs font-semibold text-gray-700">Processing Throughput</p>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {simState === 'draining' ? 'Drain mode — parallel channels' : 'Parallel channel processing'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-lg font-black font-mono ${throughput >= 5 ? 'text-emerald-600' : throughput >= 1 ? 'text-amber-600' : 'text-gray-400'}`}>
                {throughput.toFixed(1)}
              </p>
              <p className="text-[10px] text-gray-400 font-medium">pmts/sec</p>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100">
            <div
              className={`h-full transition-all duration-700 ease-out rounded-r ${
                throughput >= 5 ? 'bg-emerald-500' : throughput >= 1 ? 'bg-amber-500' : 'bg-gray-300'
              }`}
              style={{ width: `${Math.min(100, (throughput / 15) * 100)}%` }}
            />
          </div>
        </Card>
      )}

      {/* Liquidity Requirement Chart */}
      <Card className="p-0 overflow-hidden bg-white border border-gray-200 rounded-lg" data-testid="liquidity-chart">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Total Liquidity Requirement</h2>
          <p className="text-xs text-gray-500 mt-0.5">Net pending outbound by channel (debit − credit) — rolling 6-snapshot window</p>
        </div>
        <div className="p-4">
          {chartData.length === 0 ? (
            <div className="flex items-center justify-center h-60 text-sm text-gray-400">Initialize the simulation to see data</div>
          ) : (
            <div className={`relative ${allChartZero ? '' : ''}`}>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="t" stroke="#9CA3AF" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} interval={0} />
                  <YAxis stroke="#9CA3AF" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} tickFormatter={v => `$${(v / 1000000).toFixed(1)}M`} width={48} />
                  <RechartsTooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '11px', fontFamily: 'JetBrains Mono' }} formatter={(v) => [fmt(v), '']} />
                  <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: '11px' }} />
                  {channels.map((ch, i) => (
                    <Bar key={ch.id} dataKey={ch.code} name={ch.name} stackId="req" fill={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} />
                  ))}
                  <Line type="monotone" dataKey="total" name="Total Requirement" stroke="#1D4ED8" strokeWidth={3} dot={false} activeDot={{ r: 4, fill: '#1D4ED8' }} />
                </ComposedChart>
              </ResponsiveContainer>
              {allChartZero && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-xs text-gray-400 bg-white/80 px-3 py-1.5 rounded-full border border-gray-200">No current pending debits — all processed or netted</span>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Netting Time-Series Chart — only shown for Multilateral Netting algorithm */}
      {nettingTimeline.length > 0 && config?.selected_algorithm === 'MULTILATERAL_NETTING' && (
        <Card className="p-0 overflow-hidden bg-white border border-gray-200 rounded-lg" data-testid="netting-timeseries-chart">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">Netting Efficiency Over Time</h2>
            <p className="text-xs text-gray-500 mt-0.5">Bilateral savings % and payment netting efficiency per cycle</p>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={nettingTimeline} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="cycle" stroke="#9CA3AF" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} label={{ value: 'Cycle', position: 'insideBottom', offset: -2, style: { fontSize: 10 } }} />
                <YAxis stroke="#9CA3AF" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} tickFormatter={v => `${v}%`} width={38} domain={[0, 100]} />
                <RechartsTooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '11px', fontFamily: 'JetBrains Mono' }}
                  formatter={(v, name) => [`${v}%`, name]}
                  labelFormatter={(l) => `Cycle ${l}`}
                />
                <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: '11px' }} />
                <ReferenceLine y={50} stroke="#E5E7EB" strokeDasharray="4 2" />
                <Line type="monotone" dataKey="savings_pct" name="Bilateral Savings %" stroke="#10B981" strokeWidth={2.5} dot={{ r: 4, fill: '#10B981' }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="netting_eff" name="Payment Efficiency %" stroke="#6366F1" strokeWidth={2.5} dot={{ r: 4, fill: '#6366F1' }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* KPI Trend Chart — non-netting algorithms only */}
      {config?.selected_algorithm !== 'MULTILATERAL_NETTING' && kpiSnapshots.length > 1 && (
        <Card className="p-0 overflow-hidden bg-white border border-gray-200 rounded-lg" data-testid="kpi-trend-chart">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">KPI Trend</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              <span className="text-emerald-600 font-semibold">SER %</span> &nbsp;·&nbsp;
              <span className="text-red-500 font-semibold">BDR %</span> &nbsp;·&nbsp;
              <span className="text-indigo-500 font-semibold">LPR ×</span> &nbsp;over simulation time
            </p>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={kpiSnapshots} margin={{ top: 4, right: 44, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="t" stroke="#9CA3AF" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} interval="preserveStartEnd" />
                <YAxis
                  yAxisId="pct"
                  stroke="#9CA3AF"
                  tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }}
                  tickFormatter={v => `${v}%`}
                  width={44}
                  domain={[0, 100]}
                />
                <YAxis
                  yAxisId="ratio"
                  orientation="right"
                  stroke="#9CA3AF"
                  tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }}
                  tickFormatter={v => `${v}x`}
                  width={38}
                />
                <RechartsTooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '11px', fontFamily: 'JetBrains Mono' }}
                  formatter={(v, name) => [name === 'LPR' ? `${v}x` : `${v}%`, name]}
                />
                <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: '11px' }} />
                <Line yAxisId="pct"   type="monotone" dataKey="ser" name="SER" stroke="#10B981" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#10B981' }} />
                <Line yAxisId="pct"   type="monotone" dataKey="bdr" name="BDR" stroke="#EF4444" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#EF4444' }} />
                <Line yAxisId="ratio" type="monotone" dataKey="lpr" name="LPR" stroke="#6366F1" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#6366F1' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50" data-testid="kpi-trend-footer">
            <p className="text-[10px] text-gray-400 leading-relaxed">
              <span className="font-semibold text-emerald-600">SER</span> near 100% = near-complete settlement &nbsp;&middot;&nbsp;
              <span className="font-semibold text-red-500">BDR</span> near 0% = minimal external borrowing &nbsp;&middot;&nbsp;
              <span className="font-semibold text-indigo-500">LPR</span> &gt; 1x = each unit of liquidity settled more than its face value
            </p>
          </div>
        </Card>
      )}

    </div>
  );
};
